<template>
  <v-container fluid>
    <breadcrumb :items="breadcrumbs" />
    <v-row class="align-center justify-space-between mb-10">
      <v-col cols="8">
        <h1 class="font-weight-bold"> {{ isEditMode ? 'Edit Plot Tree Mapping Request' : 'Add New Plot Tree Mapping Request' }} </h1>
      </v-col>
      <v-col cols="4" class="text-right">
        <v-btn text color="primary" outlined @click="cancelForm">Cancel</v-btn> &nbsp;
        <v-btn color="primary" dark @click="submitRequest"> {{ isEditMode ? 'Update Request' : 'Submit Request' }}</v-btn>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-card-text>
        <v-form ref="form" v-model="isFormValid">
          <v-row>
            <v-col cols="12" md="3">
              <label>{{ $t('carbonCredit.plotTreeMapping.project') }}</label>
              <v-autocomplete
                v-model="selectedProjectId"
                :items="projectOptions"
                :item-text="formatProjectOptionLabel"
                item-value="id"
                :placeholder="$t('carbonCredit.plotTreeMapping.selectProject')"
                outlined
                dense
                clearable
                :loading="projectsLoading"
                :rules="[v => !!v || $t('carbonCredit.plotTreeMapping.projectRequired')]"
                @change="onProjectChange"
              />
            </v-col>

            <v-col cols="12" md="4" class="assign-to-col">
              <label>Assign To</label>
              <div class="d-flex assign-to-fields">
                <v-autocomplete
                  :items="roles"
                  item-text="name"
                  item-value="id"
                  placeholder="Select Role"
                  outlined
                  dense
                  v-model="selectedRole"
                  @change="getUsersByRole"
                  class="assign-to-role"
                />
                <v-autocomplete
                  :items="assigneeSearchResults"
                  item-text="fullName"
                  item-value="userId"
                  placeholder="Select Name"
                  outlined
                  dense
                  v-model="selectedAssignee"
                  :search-input.sync="assigneeSearchInput"
                  :loading="assigneesSearchLoading"
                  no-filter
                  @update:search-input="onAssigneeSearchInput"
                  @focus="onAssigneeFieldFocus"
                  :disabled="!selectedRole"
                  class="assign-to-name"
                />
              </div>
            </v-col>

            <v-col cols="12" md="2">
              <label>Farmer</label>
              <v-autocomplete
                :items="farmerOptions"
                item-text="fullName"
                item-value="userId"
                :placeholder="$t('select')"
                outlined
                dense
                clearable
                v-model="selectedFarmerId"
                :loading="farmersLoading"
                :disabled="!selectedProjectId"
                @change="onFarmerChange"
                :rules="[v => !!v || 'Farmer is required']"
              />
            </v-col>

            <v-col cols="12" md="3">
              <label>{{ $t('carbonCredit.plotTreeMapping.farm') }}</label>
              <v-autocomplete
                :items="farmNames"
                item-text="farmName"
                item-value="id"
                :placeholder="$t('select')"
                :loading="farmNamesLoading"
                outlined
                dense
                clearable
                v-model="selectedFarmId"
                :disabled="!selectedProjectId || !selectedFarmerId"
                :rules="[v => !!v || 'Farm is required']"
                @change="onFarmSelected"
              />
            </v-col>
          </v-row>

          <!-- Row 2: Start Date | Due Date | Farm Location | Additional Notes -->
          <v-row>
            <v-col cols="12" md="3" class="start-due-date-col">
              <label>Start Date</label>
              <v-menu
                ref="startDateMenu"
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    :placeholder="$t('select')"
                    v-on="on"
                    :rules="[v => !!v || 'Start date is required']"
                  >
                    <template v-slot:append>
                      <v-icon size="22" class="date-field-append-icon" @click="onStartDateAppendClick">
                        {{ startDate ? 'mdi-close' : 'mdi-calendar' }}
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker v-model="startDate" no-title @input="startDateMenu = false" />
              </v-menu>
            </v-col>

            <v-col cols="12" md="3" class="start-due-date-col">
              <label>Due Date</label>
              <v-menu
                ref="dueDateMenu"
                v-model="dueDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dueDate"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                    :placeholder="$t('select')"
                    :rules="[v => !!v || 'Due date is required']"
                  >
                    <template v-slot:append>
                      <v-icon size="22" class="date-field-append-icon" @click="onDueDateAppendClick">
                        {{ dueDate ? 'mdi-close' : 'mdi-calendar' }}
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker v-model="dueDate" no-title @input="dueDateMenu = false" />
              </v-menu>
            </v-col>

            <v-col cols="12" md="3" class="farm-location-col">
              <label>Farm Location Address</label>
              <div @click="farmLocationHandler" class="farm-location-wrapper">
                <v-text-field
                  @click="farmLocationHandler"
                  readonly
                  id="dialog-link1"
                  placeholder="Please click to choose location"
                  outlined
                  dense
                  :value="farmLocationAddress"
                  class="farm-location-input"
                />
              </div>
            </v-col>

            <v-col cols="12" md="3">
              <label>Additional Notes</label>
              <v-text-field
                v-model="notes"
                :placeholder="$t('enterHere')"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <!-- Plot table -->
          <v-row>
            <v-col cols="12">
              <v-data-table
                :headers="plotHeaders"
                :items="sortedSelectedPlot"
                class="mt-2 plot-tree-table"
                dense
                hide-default-footer
                :no-data-text="tableNoDataText"
              >
                <template v-slot:item.radius="{ item }">
                  <span>{{ formatRadiusMeters(item.radius) }}</span>
                </template>
                <template v-slot:item.status="{ item }">
                  <div class="d-flex align-center" style="min-height: 40px;">
                    <v-btn
                      xs-small
                      rounded
                      depressed
                      :class="getPlotStatusClass(getPlotStatus(item))"
                      height="24"
                      min-width="70"
                      class="text-capitalize"
                    >
                      {{ formatPlotStatus(getPlotStatus(item)) }}
                    </v-btn>
                  </div>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn text small color="error" @click="removePlot(item)">Remove</v-btn>
                </template>
              </v-data-table>
            </v-col>
          </v-row>

          <!-- Select plot + Add new plot -->
          <v-row class="align-end mt-2">
            <v-col cols="12" md="4">
              <label>Select Plot Number</label>
              <v-autocomplete
                :items="plotOptions"
                v-model="selectedPlot"
                item-text="plot_no"
                :placeholder="$t('select')"
                outlined
                dense
                multiple
                chips
                deletable-chips
                return-object
                :disabled="!selectedFarmId"
              />
            </v-col>
            <v-col cols="12" md="8" class="d-flex align-end justify-end">
              <v-btn color="primary" dark @click="plotLocation">ADD NEW PLOT</v-btn>
            </v-col>
          </v-row>
        </v-form>
        <v-divider class="my-4" />
      
      </v-card-text>
    </v-card>

    <v-card outlined class="mt-6">
      <v-card-text>
        <p
          v-if="projectKmlPolygons.length"
          class="text-caption grey--text text--darken-1 mb-2"
        >
          {{ $t('carbonCredit.plotTreeMapping.projectBoundaryHint') }}
        </p>
        <div class="plot-tree-map-wrapper" :class="{ 'plot-tree-map-wrapper--loading': projectKmlLoading }">
          <v-progress-linear
            v-if="projectKmlLoading"
            indeterminate
            color="primary"
            class="plot-tree-map-loader"
          />
          <GmapMap
            ref="gmap"
            :center="mapCenter"
            :zoom="mapZoom"
            map-type-id="satellite"
            style="width: 100%; height: 500px"
          >
            <GmapPolygon
              v-for="(poly, idx) in projectKmlPolygons"
              :key="`project-kml-${idx}`"
              :paths="poly"
              :options="projectKmlPolygonOptions"
            />
            <GmapCluster v-if="plotOptions.length">
              <GmapMarker
                v-for="plot in plotOptions"
                :key="plot.id"
                :position="{ lat: parseFloat(plot.latitude), lng: parseFloat(plot.longitude) }"
                :clickable="true"
                :icon="getPlotIcon(plot)"
                @click="togglePlotSelection(plot)"
                :title="String(plot.plot_no || '')"
              />
            </GmapCluster>
          </GmapMap>
        </div>

        <div class="status-legend-section plot-tree-status-legend mt-4 pa-4">
          <div class="legend-title font-weight-bold mr-3 d-flex align-center flex-wrap">
            {{ $t('carbonCredit.plotTreeMapping.plotLegendTitle') }}
            <v-tooltip top color="#00BD73" max-width="350">
              <template v-slot:activator="{ on, attrs }">
                <v-icon size="16" class="icon-with-background primary--text ml-1" v-bind="attrs" v-on="on">
                  mdi-information
                </v-icon>
              </template>
              <span>{{ $t('carbonCredit.plotTreeMapping.plotMarkerLegendInfo') }}</span>
            </v-tooltip>
          </div>
          <div class="legend-item d-inline-flex align-center flex-wrap mr-4 mt-2">
            <span
              v-for="item in plotMarkerLegendItems"
              :key="item.key"
              class="legend-item d-inline-flex align-center mr-4 mt-2"
            >
              <img
                :src="item.iconDataUrl"
                alt=""
                class="plot-marker-legend-icon flex-shrink-0"
              >
              <span>{{ $t(item.labelKey) }}</span>
            </span>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import CarbonCreditService from '@/_services/CarbonCreditService';
import loading from "@/mixins/LoadingMixin";
import RoleMixin from "@/mixins/RoleMixin";
import GmapCluster from 'vue2-google-maps/dist/components/cluster';
import { DEFAULT_PLOT_SVG_RAW, SELECTED_PLOT_SVG_RAW } from '@/utils/plotMarkerIconSvgs';
import {
  fetchAndParseKmlFromUrl,
  PROJECT_BOUNDARY_POLYGON_OPTIONS,
} from '@/utils/kmlParser';

const PLOT_MARKER_CATEGORY_COLORS = {
  yellow: '#F9A825',
  blue: '#1976D2',
  green: '#2E7D32',
};

/** Same as PlotTreeMappingView — matches Google Maps scaledSize and legend icon size. */
const PLOT_MAP_MARKER_PIXEL_SIZE = 40;

export default {
  components: { GmapCluster },
  mixins: [loading, RoleMixin],

  async created() {
    this.requestId = this.$route.params.id;
    this.isEditMode = !!this.requestId;
    const draft = this.$store.getters['carbonCredit/getAddPlotTreeDraft'];
    const isReturningFromAddFlow = !this.isEditMode && draft && this.hasExpandedDraftData(draft);

    if (isReturningFromAddFlow) {
      const selectedPlotIds = draft.selectedPlotIds;
      this.restoreDraftFromStore(true);
      const newlyAdded = this.$store.getters['carbonCredit/getNewlyAddedPlot'];
      if (newlyAdded && newlyAdded.id) {
        this.mergeNewlyAddedPlot(newlyAdded);
        this.$store.dispatch('carbonCredit/clearNewlyAddedPlot');
      }
      const needsPlotFetch = this.farmLocation?.latitude != null && this.plotOptions.length === 0;
      if (needsPlotFetch) {
        this.startLoading();
        try {
          await this.getTreePlots();
          if (Array.isArray(this.plotOptions) && this.plotOptions.length > 0) {
            if (Array.isArray(selectedPlotIds) && selectedPlotIds.length > 0) {
              this.selectedPlot = this.plotOptions.filter(p => selectedPlotIds.includes(p.id));
            } else {
              this.selectedPlot = [...this.plotOptions];
            }
          }
        } finally {
          this.stopLoading();
        }
      }
      this.$nextTick(() => this.fitMapToBounds());
      return;
    }

    this.startLoading();
    try {
      await this.fetchProjects();
      await this.getRoles();
      if (!this.isEditMode) {
        await this.getTreePlots();
      }
      if (this.isEditMode) {
        await this.loadRequestData();
        const farmLocationJustSaved = this.$store.getters['carbonCredit/getFarmLocationJustSaved'];
        if (farmLocationJustSaved && this.farmLocation?.latitude != null) {
          const plotsBeforeSearch = [...this.selectedPlot];
          await this.getTreePlots();
          // Merge: keep existing request plots (may be outside new farm area) + add new from search
          const existingIds = new Set(plotsBeforeSearch.map(p => Number(p.id)));
          const newFromSearch = (this.plotOptions || []).filter(p => !existingIds.has(Number(p.id)));
          this.selectedPlot = [...plotsBeforeSearch, ...newFromSearch];
          const byId = new Map();
          // Add search results first, then request plots - so submitted plot info (trees, attachments) is preserved
          [...(this.plotOptions || []), ...plotsBeforeSearch].forEach(p => byId.set(Number(p.id), p));
          this.plotOptions = Array.from(byId.values());
          this.$store.dispatch('carbonCredit/clearFarmLocationJustSaved');
        }
        this.$nextTick(() => this.fitMapToBounds());
      } else {
        await this.restoreDraftFromStore(false);
      }
      const pendingEdit = this.$store.getters['carbonCredit/getPendingEditSelectedPlots'];
      const newlyAdded = this.$store.getters['carbonCredit/getNewlyAddedPlot'];
      if (this.isEditMode && pendingEdit && (Array.isArray(pendingEdit.selectedPlot) || Array.isArray(pendingEdit.plotOptions))) {
        if (Array.isArray(pendingEdit.selectedPlot) && pendingEdit.selectedPlot.length > 0) {
          this.selectedPlot = [...pendingEdit.selectedPlot];
        }
        if (Array.isArray(pendingEdit.plotOptions) && pendingEdit.plotOptions.length > 0) {
          this.plotOptions = [...pendingEdit.plotOptions];
        }
        this.$store.dispatch('carbonCredit/clearPendingEditSelectedPlots');
      }
      if (newlyAdded && newlyAdded.id) {
        this.mergeNewlyAddedPlot(newlyAdded);
        this.$store.dispatch('carbonCredit/clearNewlyAddedPlot');
      }
    } finally {
      this.stopLoading();
    }
  },

  async activated() {
    const newlyAdded = this.$store.getters['carbonCredit/getNewlyAddedPlot'];
    const pendingEdit = this.$store.getters['carbonCredit/getPendingEditSelectedPlots'];
    const farmLocationJustSaved = this.$store.getters['carbonCredit/getFarmLocationJustSaved'];
    const hasAsyncWork =
      (this.isEditMode && (pendingEdit || (farmLocationJustSaved && this.farmLocation?.latitude != null))) ||
      (!this.isEditMode && (this.farmLocation?.latitude != null || (this.plotOptions && this.plotOptions.length) > 0)) ||
      newlyAdded;
    if (!hasAsyncWork) return;

    this.startLoading();
    try {
      if (this.isEditMode && pendingEdit && (Array.isArray(pendingEdit.selectedPlot) || Array.isArray(pendingEdit.plotOptions))) {
        if (Array.isArray(pendingEdit.selectedPlot) && pendingEdit.selectedPlot.length > 0) {
          this.selectedPlot = [...pendingEdit.selectedPlot];
        }
        if (Array.isArray(pendingEdit.plotOptions) && pendingEdit.plotOptions.length > 0) {
          this.plotOptions = [...pendingEdit.plotOptions];
        }
        this.$store.dispatch('carbonCredit/clearPendingEditSelectedPlots');
      }
      if (this.isEditMode && farmLocationJustSaved && this.farmLocation?.latitude != null) {
        const plotsBeforeSearch = [...this.selectedPlot];
        await this.getTreePlots();
        const existingIds = new Set(plotsBeforeSearch.map(p => Number(p.id)));
        const newFromSearch = (this.plotOptions || []).filter(p => !existingIds.has(Number(p.id)));
        this.selectedPlot = [...plotsBeforeSearch, ...newFromSearch];
        const byId = new Map();
        // Add search results first, then request plots - so submitted plot info (trees, attachments) is preserved
        [...(this.plotOptions || []), ...plotsBeforeSearch].forEach(p => byId.set(Number(p.id), p));
        this.plotOptions = Array.from(byId.values());
        this.$store.dispatch('carbonCredit/clearFarmLocationJustSaved');
      } else if (!this.isEditMode) {
        const hasFarmLocation = this.farmLocation?.latitude != null;
        if (hasFarmLocation || this.plotOptions?.length) {
          await this.getTreePlots();
          if (Array.isArray(this.plotOptions) && this.plotOptions.length > 0) {
            this.selectedPlot = [...this.plotOptions];
          }
        }
      }
      if (newlyAdded && newlyAdded.id) {
        this.mergeNewlyAddedPlot(newlyAdded);
        this.$store.dispatch('carbonCredit/clearNewlyAddedPlot');
      }
      this.$nextTick(() => this.fitMapToBounds());
    } finally {
      this.stopLoading();
    }
  },

  beforeRouteLeave(to, from, next) {
    if (this.isEditMode && to.name === 'plotLocation') {
      this.$store.dispatch('carbonCredit/setPendingEditSelectedPlots', {
        selectedPlot: [...(this.selectedPlot || [])],
        plotOptions: [...(this.plotOptions || [])]
      });
    } else if (!this.isEditMode) {
      const saveDraftRoutes = ['ChooseFarmLocation', 'plotLocation'];
      if (saveDraftRoutes.includes(to.name)) {
        this.saveDraftToStore();
      } else {
        this.clearFormData();
      }
    }
    next();
  },

  data() {
    return {
      fullPage: true,
      breadcrumbs: [
        { text: 'Dashboard', disabled: false },
        { text: 'Carbon Credit', disabled: true },
        { text: 'Plot Tree Maping', disabled: true },
      ],
      roles: [],
      assignees: [],
      assigneeSearchResults: [],
      assigneeSearchInput: '',
      assigneesSearchLoading: false,
      assigneeSearchDebounce: null,
      farmNames: [],
      farmNamesLoading: false,
      selectedRole: null,
      selectedAssignee: null,
      selectedProjectId: null,
      projectOptions: [],
      projectsLoading: false,
      selectedFarmerId: null,
      farmerOptions: [],
      farmersLoading: false,
      selectedFarmId: null,
      plotOptions: [],
      startDate: null,
      dueDate: null,
      notes: '',
      startDateMenu: false,
      dueDateMenu: false,
      selectedPlot: [],
      isFormValid: false,
      plotHeaders: [
        { text: 'Plot Number', value: 'plot_no' },
        { text: 'Longitude', value: 'longitude' },
        { text: 'Latitude', value: 'latitude' },
        { text: 'Radius', value: 'radius' },
        { text: 'Status', value: 'status' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'end' },
      ],
      defaultPlotIcon: '/assets/icons/defaultPlot.svg',
      selectedPlotIcon: '/assets/icons/selectedPlot.svg',
      isEditMode: false,
      requestId: null,
      requestCountry: null,
      projectKmlPolygons: [],
      projectKmlMarkers: [],
      projectKmlLoading: false,
      mapCenter: { lat: 20.5937, lng: 78.9629 },
      mapZoom: 4,
      projectKmlPolygonOptions: PROJECT_BOUNDARY_POLYGON_OPTIONS,
    };
  },
  computed: {
    ...mapGetters({
      farmLocation: 'carbonCredit/getFarmLocation',
      farmList: 'memberData/farms',
    }),
    // The project chosen on this screen.
    plotTreeSelectedProject() {
      const id = this.selectedProjectId ?? this.filters?.projectId;
      const options = this.projectOptions || this.projectFilterOptions || [];
      return options.find((p) => Number(p.id) === Number(id)) || null;
    },
    selectedProjectTitle() {
      return this.plotTreeSelectedProject?.project_title || '';
    },
    isSuperAdmin() {
      return this.getCurrentRoles && Array.isArray(this.getCurrentRoles) &&
        this.getCurrentRoles.some(role => role === 'super_admin');
    },
    farmLocationAddress() {
      return this.farmLocation ? this.farmLocation.address : null;
    },
    sortedSelectedPlot() {
      const list = this.selectedPlot || [];
      return [...list].sort((a, b) => {
        const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bDate - aDate;
      });
    },
    tableNoDataText() {
      if (!this.farmLocation && this.selectedPlot.length === 0) {
        return 'Set farm location to load plots, or add a new plot.';
      }
      if (this.selectedPlot.length === 0) {
        return 'No plots found for this farm location. Click "ADD NEW PLOT" to add one.';
      }
      return 'No plots';
    },
    plotMarkerLegendItems() {
      const raw = SELECTED_PLOT_SVG_RAW;
      const c = PLOT_MARKER_CATEGORY_COLORS;
      return [
        {
          key: 'measured',
          iconDataUrl: this.coloredPlotIconDataUrl(raw, c.blue),
          labelKey: 'carbonCredit.plotTreeMapping.measuredPlot',
        },
        {
          key: 'suggested',
          iconDataUrl: this.coloredPlotIconDataUrl(raw, c.yellow),
          labelKey: 'carbonCredit.plotTreeMapping.suggestedPlot',
        },
        {
          key: 'audited',
          iconDataUrl: this.coloredPlotIconDataUrl(raw, c.green),
          labelKey: 'carbonCredit.plotTreeMapping.auditedPlot',
        },
      ];
    },
  },
  methods: {
    plotTreeMappingApiContext() {
      return {
        selectedProjectId: this.selectedProjectId,
        projectOptions: this.projectOptions,
        country: this.requestCountry,
      };
    },
    formatProjectOptionLabel(project) {
      return project?.project_title || '';
    },
    async fetchProjects() {
      this.projectsLoading = true;
      try {
        const response = await CarbonCreditService.getPlotTreeMappingProjects({
          limit: 500,
        });
        if (response?.success !== false) {
          this.projectOptions = response.data || [];
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        this.projectOptions = [];
        this.$notify({
          type: 'error',
          text: this.$t('carbonCredit.plotTreeMapping.errorLoadingProjects'),
        });
      } finally {
        this.projectsLoading = false;
      }
    },
    onProjectChange() {
      const project = this.plotTreeSelectedProject;
      this.selectedProjectId = project ? Number(project.id) : null;
      this.selectedFarmerId = null;
      this.selectedFarmId = null;
      this.farmNames = [];
      this.farmerOptions = [];
      this.selectedPlot = [];
      this.plotOptions = [];
      this.$store.dispatch('carbonCredit/clearFarmLocation');
      this.clearProjectKmlBoundary();
      if (this.selectedProjectId) {
        this.getFarmersByProject();
        this.loadProjectKmlBoundary();
      }
    },
    clearProjectKmlBoundary() {
      this.projectKmlPolygons = [];
      this.projectKmlMarkers = [];
      this.mapCenter = { lat: 20.5937, lng: 78.9629 };
      this.mapZoom = 4;
      this.$store.dispatch('carbonCredit/clearProjectKmlBoundary');
    },
    syncProjectKmlBoundaryToStore() {
      if (!this.projectKmlPolygons.length) {
        this.$store.dispatch('carbonCredit/clearProjectKmlBoundary');
        return;
      }
      this.$store.dispatch('carbonCredit/setProjectKmlBoundary', {
        polygons: this.projectKmlPolygons.map((poly) =>
          poly.map((p) => ({ lat: p.lat, lng: p.lng }))
        ),
        projectTitle: this.selectedProjectTitle,
      });
    },
    async loadProjectKmlBoundary() {
      if (!this.selectedProjectId) {
        this.clearProjectKmlBoundary();
        return;
      }
      this.projectKmlLoading = true;
      try {
        const response = await CarbonCreditService.getCarbonCreditProjectById(
          this.selectedProjectId
        );
        const project = response?.data ?? response;
        const kmlUrl = project?.kml_file || null;
        if (!kmlUrl) {
          this.clearProjectKmlBoundary();
          return;
        }
        const { polygons, markers, center, zoom } = await fetchAndParseKmlFromUrl(kmlUrl);
        this.projectKmlPolygons = polygons || [];
        this.projectKmlMarkers = markers || [];
        if (center) {
          this.mapCenter = { ...center };
          this.mapZoom = zoom;
        }
        this.syncProjectKmlBoundaryToStore();
        this.$nextTick(() => this.fitMapToBounds());
      } catch (err) {
        console.warn('Could not load project KML boundary:', err);
        this.clearProjectKmlBoundary();
        this.$notify({
          type: 'warning',
          text: this.$t('carbonCredit.plotTreeMapping.errorLoadingProjectBoundary'),
        });
      } finally {
        this.projectKmlLoading = false;
      }
    },
    async getFarmersByProject() {
      if (!this.selectedProjectId) {
        this.farmerOptions = [];
        return;
      }
      this.farmersLoading = true;
      try {
        const response = await CarbonCreditService.getPlotTreeMappingProjectFarmers({
          project_id: this.selectedProjectId,
        });
        if (response?.success !== false) {
          this.farmerOptions = (response.data || []).map((f) => ({
            ...f,
            userId: Number(f.userId),
          }));
        } else {
          this.farmerOptions = [];
        }
      } catch (e) {
        console.error('Error loading project farmers:', e);
        this.farmerOptions = [];
        this.$notify({
          type: 'error',
          text: this.$t('carbonCredit.plotTreeMapping.errorLoadingFarmers'),
        });
      } finally {
        this.farmersLoading = false;
      }
    },
    onFarmerChange() {
      this.selectedFarmId = null;
      this.selectedPlot = [];
      this.plotOptions = [];
      this.$store.dispatch('carbonCredit/clearFarmLocation');
      if (this.selectedProjectId && this.selectedFarmerId) {
        this.getFarmsByProject();
      } else {
        this.farmNames = [];
      }
    },
    async getFarmsByProject() {
      if (!this.selectedProjectId || !this.selectedFarmerId) {
        this.farmNames = [];
        this.selectedFarmId = null;
        this.$store.dispatch('carbonCredit/clearFarmLocation');
        this.plotOptions = [];
        this.selectedPlot = [];
        return;
      }
      this.farmNamesLoading = true;
      try {
        const response = await CarbonCreditService.getPlotTreeMappingProjectFarms({
          project_id: this.selectedProjectId,
          farmer_id: this.selectedFarmerId,
        });
        if (response?.success !== false) {
          this.farmNames = response.data || [];
        } else {
          this.farmNames = [];
        }
      } catch (e) {
        console.error('Error loading project farms:', e);
        this.farmNames = [];
        this.$notify({
          type: 'error',
          text: this.$t('carbonCredit.plotTreeMapping.errorLoadingFarms'),
        });
      } finally {
        this.farmNamesLoading = false;
      }
    },
    async getRoles() {
      try {
        const cached = this.$store.getters['carbonCredit/getCachedUserMembershipRoleResponse'];
        let data;
        if (cached && cached.data && Array.isArray(cached.data.listRes)) {
          data = cached;
        } else {
          const res = await this.$http.get("/user/membership/user-role");
          data = res.data;
          this.$store.commit('carbonCredit/SET_USER_MEMBERSHIP_ROLE_RESPONSE', data);
        }
        const roles = (data.data?.listRes || []).filter(x => {
          if (x.id == 'avocado_farmer') return false;
          return true;
        });
        this.roles = roles;
      } catch (e) {
        console.log(e);
      }
    },
    async getUsersByRole(opts = {}) {
      if (!this.selectedRole) return;
      const { skipLoading, preserveAssignee } = opts;
      if (!preserveAssignee) {
        this.selectedAssignee = null;
        this.assigneeSearchResults = [];
        this.assigneeSearchInput = '';
      }
      if (!skipLoading) this.startLoading();
      try {
        await this.searchUsersByRole('');
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Failed to fetch users. Please try again.'
        });
        this.assigneeSearchResults = [];
      } finally {
        if (!skipLoading) this.stopLoading();
      }
    },
    formatAssignee(user) {
      const userId = user.userId ?? user.id;
      const fullName = user.fullName ?? (`${user.first_name || user.firstName || ''} ${user.last_name || user.lastName || ''}`.trim() || `User ${userId}`);
      return { ...user, userId, fullName };
    },
    onAssigneeSearchInput(val) {
      if (!this.selectedRole) return;
      if (this.assigneeSearchDebounce) clearTimeout(this.assigneeSearchDebounce);
      const trimmed = (val || '').trim();
      if (this.selectedAssignee && trimmed) {
        const selected = this.assigneeSearchResults.find(u => String(u.userId) === String(this.selectedAssignee));
        if (selected && selected.fullName && trimmed === selected.fullName) return;
      }
      this.assigneeSearchDebounce = setTimeout(() => {
        this.searchUsersByRole(val || '');
      }, 300);
    },
    onAssigneeFieldFocus() {
      if (!this.selectedRole) return;
      if (this.assigneeSearchResults.length === 0 && !this.assigneesSearchLoading) {
        this.searchUsersByRole(this.assigneeSearchInput || '');
      }
    },
    async searchUsersByRole(searchPhrase) {
      if (!this.selectedRole) return;
      this.assigneesSearchLoading = true;
      try {
        const res = await CarbonCreditService.getUsersByRole({
          role_id: this.selectedRole,
          page: 1,
          limit: 1000,
          name: (searchPhrase || '').trim()
        });
        const raw = res?.data ?? (Array.isArray(res) ? res : []);
        const list = Array.isArray(raw) ? raw : [];
        let results = list.map(u => this.formatAssignee(u)).filter(u => u.userId != null);
        if (this.selectedAssignee) {
          const hasSelected = results.some(u => String(u.userId) === String(this.selectedAssignee));
          if (!hasSelected) {
            const selectedFromCurrent = this.assigneeSearchResults.find(
              u => String(u.userId) === String(this.selectedAssignee)
            );
            if (selectedFromCurrent) {
              results = [selectedFromCurrent, ...results];
            } else if (this._restoredAssigneeName) {
              results = [{ userId: this.selectedAssignee, fullName: this._restoredAssigneeName || `User ${this.selectedAssignee}` }, ...results];
            }
            this._restoredAssigneeName = null;
          }
        }
        this.assigneeSearchResults = results;
      } catch (error) {
        console.error('Error searching users:', error);
        this.assigneeSearchResults = [];
        this.$notify({ type: 'error', text: 'Failed to search users' });
      } finally {
        this.assigneesSearchLoading = false;
      }
    },
    onFarmSelected() {
      if (!this.selectedFarmId) {
        this.$store.dispatch('carbonCredit/clearFarmLocation');
        this.selectedPlot = [];
        this.plotOptions = [];
        return;
      }
      const farm = Array.isArray(this.farmNames)
        ? this.farmNames.find(f => String(f.id) === String(this.selectedFarmId))
        : null;
      if (!farm) return;
      const lat = farm.latitude ?? farm.lat;
      const lng = farm.longitude ?? farm.log;
      if (lat != null && lng != null) {
        const hasArea = farm.area != null && farm.area !== '';
        const farmLocation = {
          latitude: parseFloat(lat),
          longitude: parseFloat(lng),
          address: farm.address || null,
          areaSize: hasArea ? String(farm.area) : '',
          areaUnit: hasArea ? 'acres' : undefined, // farm.area from API is in acres
          country: farm.country || null,
        };
        this.$store.dispatch('carbonCredit/setFarmLocation', farmLocation);
        // In edit mode, don't search plots until user saves from Farm Location
        if (!this.isEditMode) {
          this.getTreePlots();
        }
      } else {
        this.$store.dispatch('carbonCredit/clearFarmLocation');
        this.plotOptions = [];
      }
    },
    plotLocation() {
      this.syncProjectKmlBoundaryToStore();
      this.$router.push({
        name: 'plotLocation',
        query: this.requestId ? { requestId: this.requestId } : {}
      });
    },
    farmLocationHandler() {
      this.$router.push({
        name: 'ChooseFarmLocation',
        query: this.requestId ? { requestId: this.requestId } : {}
      });
    },
    onStartDateAppendClick(e) {
      if (this.startDate) {
        e.stopPropagation();
        this.startDate = null;
        this.startDateMenu = false;
      }
    },
    onDueDateAppendClick(e) {
      if (this.dueDate) {
        e.stopPropagation();
        this.dueDate = null;
        this.dueDateMenu = false;
      }
    },
    mergeNewlyAddedPlot(plot) {
      if (!plot || !plot.id) return;
      const existsInOptions = this.plotOptions.some(p => p.id === plot.id);
      if (!existsInOptions) {
        this.plotOptions = [...this.plotOptions, plot];
      }
      const existsInSelected = this.selectedPlot.some(p => p.id === plot.id);
      if (!existsInSelected) {
        this.selectedPlot = [...this.selectedPlot, plot];
      }
      this.fitMapToBounds();
    },
    removePlot(plotToRemove) {
      this.selectedPlot = this.selectedPlot.filter(plot => plot.id !== plotToRemove.id);
    },
    getPlotStatus(plot) {
      return (plot && (plot.plotStatus && plot.plotStatus.status)) || plot?.status || null;
    },
    getPlotStatusClass(status) {
      switch (status) {
        case 'submitted':
          return 'green-button';
        case 'pending':
          return 'gray-button';
        case 'in_progress':
        case 'partially_submitted':
        case 'audited':
          return 'orange-button';
        default:
          return 'gray-button';
      }
    },
    formatPlotStatus(status) {
      if (!status) return 'Pending';
      const statusMap = {
        'pending': 'Pending',
        'in_progress': 'Partially Submitted',
        'partially_submitted': 'Partially Submitted',
        'submitted': 'Submitted',
        'audited': 'Audited',
      };
      return statusMap[status] || status;
    },
    getPlotMarkerCategory(plot) {
      const plotNo = String(plot?.plot_no ?? '').trim();
      const firstLetter = /^[A-Za-z]/.test(plotNo) ? plotNo.charAt(0).toUpperCase() : '';
      if (firstLetter === 'J') return 'green';
      if (firstLetter === 'M') return 'blue';
      if (firstLetter === 'S') return 'yellow';

      const status = String(this.getPlotStatus(plot) || '').toLowerCase();
      if (status === 'audited') return 'green';
      if (status === 'submitted' || status === 'partially_submitted' || status === 'in_progress') {
        return 'blue';
      }
      if (!status || status === 'pending') return 'yellow';
      return null;
    },
    coloredPlotIconDataUrl(svgRaw, colorHex) {
      const svg = svgRaw
        .replace(/#34B0F5/g, colorHex)
        .replace(/#566DE3/g, colorHex);
      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    },
    getPlotIcon(plot) {
      const isSelected = this.selectedPlot.some(p => p.id === plot.id);
      const category = this.getPlotMarkerCategory(plot);
      let url;
      if (!category) {
        url = isSelected ? this.selectedPlotIcon : this.defaultPlotIcon;
      } else {
        const raw = isSelected ? SELECTED_PLOT_SVG_RAW : DEFAULT_PLOT_SVG_RAW;
        url = this.coloredPlotIconDataUrl(raw, PLOT_MARKER_CATEGORY_COLORS[category]);
      }
      const s = PLOT_MAP_MARKER_PIXEL_SIZE;
      return {
        url,
        scaledSize: new window.google.maps.Size(s, s),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(s / 2, s),
      };
    },
    togglePlotSelection(plot) {
      const plotIndex = this.selectedPlot.findIndex(p => p.id === plot.id);
      if (plotIndex > -1) {
        this.selectedPlot.splice(plotIndex, 1);
      } else {
        this.selectedPlot.push(plot);
      }
    },
    fitMapToBounds() {
      this.$nextTick(() => {
        const hasPlots = this.plotOptions.length > 0;
        const hasProjectArea = this.projectKmlPolygons.length > 0;
        if ((!hasPlots && !hasProjectArea) || !this.$refs.gmap) return;

        this.$refs.gmap.$mapPromise.then((map) => {
          const bounds = new window.google.maps.LatLngBounds();
          this.plotOptions.forEach((plot) => {
            const lat = parseFloat(plot.latitude);
            const lng = parseFloat(plot.longitude);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              bounds.extend({ lat, lng });
            }
          });
          this.projectKmlPolygons.forEach((poly) => {
            poly.forEach((p) => bounds.extend(p));
          });
          this.projectKmlMarkers.forEach((p) => bounds.extend(p));
          if (!bounds.isEmpty()) {
            map.fitBounds(bounds);
          }
        });
      });
    },
    async getTreePlots() {
      const loc = this.farmLocation;
      const params = {};
      if (loc?.latitude != null && loc?.longitude != null) {
        params.latitude = loc.latitude;
        params.longitude = loc.longitude;
        if (loc.areaSize != null && loc.areaSize !== '' && loc.areaUnit) {
          params.areaSize = loc.areaSize;
          params.areaUnit = loc.areaUnit;
        } else {
          params.radius_km = 2;
        }
      }
      if (this.isEditMode && this.requestId) params.requestId = this.requestId;
      const hasLocationFilter = Object.keys(params).length > 0;
      const response = await CarbonCreditService.getTreePlots(params);
      if (response && response.success !== false) {
        const raw = response.data;
        const plotsList = Array.isArray(raw)
          ? raw
          : (Array.isArray(raw?.data) ? raw.data : raw?.info || []);
        this.plotOptions = plotsList;
        if (hasLocationFilter) {
          this.selectedPlot = [...plotsList];
        }
        this.fitMapToBounds();
      }
    },
    formatDateForPicker(value) {
      if (value == null || value === '') return null;
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toISOString().slice(0, 10);
      }
      const part = String(value).split('T')[0];
      return /^\d{4}-\d{2}-\d{2}$/.test(part) ? part : null;
    },
    applyFarmLocationFromRequest(data) {
      const farm = data?.farm;
      const lat = farm?.lat ?? farm?.latitude;
      const lng = farm?.log ?? farm?.longitude;
      const address = data?.farm_location_address || farm?.address || null;
      if (lat == null || lng == null) {
        if (address) {
          this.$store.dispatch('carbonCredit/setFarmLocation', {
            latitude: null,
            longitude: null,
            address,
            areaSize: '',
          });
        }
        return;
      }
      const hasArea = farm?.area != null && farm?.area !== '';
      this.$store.dispatch('carbonCredit/setFarmLocation', {
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
        address,
        areaSize: hasArea ? String(farm.area) : '',
        areaUnit: hasArea ? 'acres' : undefined,
        country: farm?.country || data?.country || null,
      });
    },
    async loadRequestData() {
      try {
        const response = await CarbonCreditService.getPlotTreeMappingRequestById(this.requestId, {});
        if (response?.success === false) return;

        const data = response?.data ?? response;
        if (!data || typeof data !== 'object') return;

        this.requestCountry = data.country ?? null;

        const projectId = data.project_id ?? data.project?.id ?? null;
        this.selectedProjectId = projectId != null ? Number(projectId) : null;
        if (data.project) {
          const projectEntry = {
            id: Number(data.project.id),
            project_title: data.project.project_title,
            country: data.project.country ?? null,
          };
          this.projectOptions = [
            projectEntry,
            ...this.projectOptions.filter((p) => Number(p.id) !== projectEntry.id),
          ];
        }

        this.selectedFarmerId = data.farmer_id != null ? Number(data.farmer_id) : null;
        this.selectedFarmId = data.farm_id != null ? Number(data.farm_id) : null;
        this.startDate = this.formatDateForPicker(data.start_date);
        this.dueDate = this.formatDateForPicker(data.due_date);
        this.notes = data.notes ?? '';

        if (Array.isArray(data.assignees) && data.assignees.length > 0) {
          const a = data.assignees[0];
          this.selectedRole = a.assignee_role ?? null;
          this.selectedAssignee = a.assignee_id != null ? Number(a.assignee_id) : null;
          if (a.assignee) {
            const formatted = this.formatAssignee({
              ...a.assignee,
              userId: a.assignee.id ?? a.assignee_id,
            });
            this.assigneeSearchResults = [formatted];
          }
          if (this.selectedRole) {
            await this.getUsersByRole({ skipLoading: true, preserveAssignee: true });
          }
        }

        this.applyFarmLocationFromRequest(data);

        let plotIds = [];
        const plotObjects = Array.isArray(data.plots)
          ? (typeof data.plots[0] === 'object' ? data.plots : [])
          : [];
        if (Array.isArray(data.plots)) {
          if (typeof data.plots[0] === 'object') {
            plotIds = data.plots.map((p) => p.id);
          } else {
            plotIds = data.plots;
          }
        }
        if (this.plotOptions.length) {
          this.selectedPlot = this.plotOptions.filter((plot) => plotIds.includes(plot.id));
        } else if (plotObjects.length > 0) {
          this.plotOptions = [...plotObjects];
          this.selectedPlot = [...plotObjects];
          this.pendingPlotIds = plotIds;
        } else {
          this.pendingPlotIds = plotIds;
        }
        if (this.selectedProjectId) {
          await Promise.all([
            this.getFarmersByProject(),
            this.loadProjectKmlBoundary(),
          ]);
          if (this.selectedFarmerId) {
            const farmerUserId = Number(this.selectedFarmerId);
            const hasFarmer = this.farmerOptions.some(f => Number(f.userId) === farmerUserId);
            if (!hasFarmer && data.farmer) {
              const fullName =
                data.farmer.fullName ||
                `${data.farmer.firstName || ''} ${data.farmer.middleName || ''} ${data.farmer.lastName || ''}`
                  .replace(/\s+/g, ' ')
                  .trim() ||
                `User ${farmerUserId}`;
              this.farmerOptions = [{ userId: farmerUserId, fullName }, ...this.farmerOptions];
            }
          }
        }

        if (this.selectedProjectId && this.selectedFarmerId) {
          await this.getFarmsByProject();
          if (data.farm && this.selectedFarmId) {
            const hasFarm = this.farmNames.some(f => Number(f.id) === Number(this.selectedFarmId));
            if (!hasFarm) {
              const f = data.farm;
              this.farmNames = [
                {
                  id: Number(f.id),
                  farmName: f.farmName,
                  latitude: f.lat,
                  longitude: f.log,
                  lat: f.lat,
                  log: f.log,
                  address: f.address,
                  area: f.area,
                  country: f.country,
                  userId: data.farmer_id != null ? Number(data.farmer_id) : f.userId,
                },
                ...this.farmNames,
              ];
            }
          }
        }

        if (this.plotOptions.length && this.pendingPlotIds) {
          this.selectedPlot = this.plotOptions.filter(plot =>
            this.pendingPlotIds.includes(plot.id)
          );
          this.pendingPlotIds = null;
        }
      } catch (error) {
        console.error('Failed to load request data:', error);
        this.$notify({ type: 'error', text: 'Failed to load request data' });
      }
    },
    async submitRequest() {
      // Validate form
      if (!this.$refs.form.validate()) {
        this.$notify({ type: 'error', text: 'Please fill in all required fields' });
        return;
      }
      if (!this.isSuperAdmin && this.selectedPlot.length === 0) {
        this.$notify({ type: 'error', text: 'Please select at least one plot' });
        return;
      }
      this.startLoading();
      try {
        const selectedFarm = Array.isArray(this.farmNames)
          ? this.farmNames.find(f => f.id === this.selectedFarmId)
          : null;

        const farm_location_address =
          this.farmLocationAddress ||
          selectedFarm?.address ||
          null;

        let attachedFarmId = this.selectedFarmId ?? null;
        if (this.isSuperAdmin && !attachedFarmId && this.farmLocation?.latitude != null && this.farmLocation?.longitude != null) {
          try {
            const checkRes = await CarbonCreditService.checkFarmLocation(
              this.farmLocation.latitude,
              this.farmLocation.longitude
            );
            const result = checkRes?.data?.data ?? checkRes?.data;
            if (result && result.inside === false) {
              this.$notify({
                type: 'error',
                text: this.$t('carbonCredit.plotTreeMapping.errorPlotOutsideRegisteredFarm')
              });
              return;
            }
            if (result?.matchedFarms?.length > 0) {
              attachedFarmId = result.matchedFarms[0].farm_id;
            }
          } catch (err) {
            this.$notify({
              type: 'error',
              text: this.$t('carbonCredit.plotTreeMapping.errorCheckingFarmLocation') || 'Could not verify farm location.'
            });
            return;
          }
        }

        const assignees =
          this.selectedAssignee && this.selectedRole
            ? [{ assignee_id: this.selectedAssignee, assignee_role: this.selectedRole }]
            : [];

        const payload = {
          project_id: this.selectedProjectId || null,
          farmer_id: this.selectedFarmerId ?? selectedFarm?.userId ?? selectedFarm?.user_id ?? null,
          farm_id: attachedFarmId ?? null,
          start_date: this.startDate,
          due_date: this.dueDate,
          notes: this.notes,
          farm_location_address,
          assignees,
          plots: this.selectedPlot.map(plot => plot.id)
        };
        if (selectedFarm?.country || this.farmLocation?.country) {
          payload.country = selectedFarm?.country || this.farmLocation?.country;
        }
        let response;
        if (this.isEditMode) {
          response = await CarbonCreditService.updatePlotTreeMapping(this.requestId, payload);
        } else {
          response = await CarbonCreditService.createPlotTreeMapping(payload);
        }
        if (response.success) {
          this.$notify({
            type: 'success',
            text: this.isEditMode
              ? 'Plot tree mapping request updated successfully'
              : 'Plot tree mapping request created successfully'
          });
          // Always clear saved form data on successful submission
          this.clearFormData();
          this.$router.push({ name: 'plotTreeMapping' });
        } else {
          this.$notify({
            type: 'error',
            text: response.message || 'Failed to save request'
          });
        }
      } catch (error) {
        const errData = error?.response?.data || error?.data;
        const errMsg = errData?.message || errData?.msg || 'An error occurred while saving the request';
        this.$notify({
          type: 'error',
          text: errMsg
        });
      } finally {
        this.stopLoading();
      }
    },
    clearFormData() {
      this.$store.dispatch('carbonCredit/clearAddPlotTreeDraft');
      this.$store.dispatch('carbonCredit/clearFarmLocation');
      this.$store.dispatch('carbonCredit/clearProjectKmlBoundary');
    },
    saveDraftToStore() {
      const selectedAssignObj = this.assigneeSearchResults.find(u => String(u.userId) === String(this.selectedAssignee));
      const selectedFarmerObj = this.farmerOptions.find(f => String(f.userId) === String(this.selectedFarmerId));
      const selectedFarmObj = this.farmNames.find(f => String(f.id) === String(this.selectedFarmId));
      this.$store.dispatch('carbonCredit/setAddPlotTreeDraft', {
        selectedProjectId: this.selectedProjectId,
        requestCountry: this.requestCountry,
        projectOptions: this.projectOptions,
        projectKmlPolygons: this.projectKmlPolygons.map((poly) =>
          poly.map((p) => ({ lat: p.lat, lng: p.lng }))
        ),
        projectTitle: this.selectedProjectTitle,
        selectedRole: this.selectedRole,
        selectedAssignee: this.selectedAssignee,
        selectedAssigneeName: selectedAssignObj?.fullName ?? null,
        selectedFarmerId: this.selectedFarmerId,
        selectedFarmerName: selectedFarmerObj?.fullName ?? null,
        selectedFarmId: this.selectedFarmId,
        selectedFarmName: selectedFarmObj?.farmName ?? null,
        selectedFarm: selectedFarmObj,
        startDate: this.startDate,
        dueDate: this.dueDate,
        notes: this.notes,
        selectedPlotIds: (this.selectedPlot || []).map(p => p.id),
        plotOptions: this.plotOptions,
        selectedPlot: this.selectedPlot,
        roles: this.roles,
        assigneeSearchResults: this.assigneeSearchResults,
        farmerOptions: this.farmerOptions,
        farmNames: this.farmNames
      });
    },
    async restoreDraftFromStore(skipApis = false) {
      const draft = this.$store.getters['carbonCredit/getAddPlotTreeDraft'];
      if (!draft) return;

      this.selectedProjectId = draft.selectedProjectId ?? null;
      this.requestCountry = draft.requestCountry ?? null;
      if (Array.isArray(draft.projectOptions) && draft.projectOptions.length > 0) {
        this.projectOptions = draft.projectOptions;
      } else if (this.selectedProjectId) {
        await this.fetchProjects();
      }
      this.selectedRole = draft.selectedRole ?? null;
      this.selectedAssignee = draft.selectedAssignee ?? null;
      this._restoredAssigneeName = draft.selectedAssigneeName ?? null;
      this.selectedFarmerId = draft.selectedFarmerId ?? null;
      this.selectedFarmId = draft.selectedFarmId ?? null;
      this.startDate = draft.startDate ?? null;
      this.dueDate = draft.dueDate ?? null;
      this.notes = draft.notes ?? '';

      if (skipApis && this.hasExpandedDraftData(draft)) {
        this.restoreFromExpandedDraft(draft);
      } else {
        const promises = [];
        if (this.selectedRole) promises.push(this.getUsersByRole({ skipLoading: true, preserveAssignee: true }));
        if (this.selectedProjectId) {
          promises.push(this.getFarmersByProject());
          promises.push(this.loadProjectKmlBoundary());
        }
        await Promise.all(promises);
        if (this.selectedProjectId && this.selectedFarmerId) {
          await this.getFarmsByProject();
        }

        if (this.selectedFarmId) {
          this.onFarmSelected();
        }
        if (Array.isArray(this.plotOptions) && this.plotOptions.length > 0) {
          this.selectedPlot = [...this.plotOptions];
        }
      }
      this.$store.dispatch('carbonCredit/clearAddPlotTreeDraft');
    },
    hasExpandedDraftData(draft) {
      return !!(draft && (draft.plotOptions !== undefined || draft.roles !== undefined || draft.farmNames !== undefined));
    },
    restoreFromExpandedDraft(draft) {
      if (Array.isArray(draft.roles) && draft.roles.length > 0) {
        this.roles = draft.roles;
      }
      if (Array.isArray(draft.assigneeSearchResults) && draft.assigneeSearchResults.length > 0) {
        this.assigneeSearchResults = draft.assigneeSearchResults;
      } else if (this.selectedAssignee && draft.selectedAssigneeName) {
        this.assigneeSearchResults = [{ userId: this.selectedAssignee, fullName: draft.selectedAssigneeName }];
      }
      if (Array.isArray(draft.farmerOptions) && draft.farmerOptions.length > 0) {
        this.farmerOptions = draft.farmerOptions;
      } else if (this.selectedFarmerId && draft.selectedFarmerName) {
        this.farmerOptions = [{ userId: this.selectedFarmerId, fullName: draft.selectedFarmerName }];
      }
      if (Array.isArray(draft.farmNames) && draft.farmNames.length > 0) {
        this.farmNames = draft.farmNames;
      }
      if (Array.isArray(draft.projectKmlPolygons) && draft.projectKmlPolygons.length > 0) {
        this.projectKmlPolygons = draft.projectKmlPolygons.map((poly) =>
          poly.map((p) => ({ lat: p.lat, lng: p.lng }))
        );
        this.syncProjectKmlBoundaryToStore();
      }
      if (Array.isArray(draft.plotOptions)) {
        this.plotOptions = draft.plotOptions;
      }
      if (Array.isArray(draft.selectedPlot)) {
        this.selectedPlot = draft.selectedPlot;
      } else if (Array.isArray(draft.selectedPlotIds) && draft.selectedPlotIds.length > 0 && this.plotOptions.length > 0) {
        this.selectedPlot = this.plotOptions.filter(p => draft.selectedPlotIds.includes(p.id));
      }
      this.$nextTick(() => this.fitMapToBounds());
    },
    cancelForm() {
      this.clearFormData();
      this.$router.push({ name: 'plotTreeMapping' });
    },
    formatRadiusMeters(value) {
      if (value === null || value === undefined || value === '') return '-';
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return value;
      const meters = numeric / 100;
      return `${meters} m`;
    }
  },
  watch: {
    plotOptions(newVal) {
      if (this.isEditMode && this.requestId && this._pendingPlotIds) {
        this.selectedPlot = newVal.filter(plot => this._pendingPlotIds.includes(plot.id));
        this._pendingPlotIds = null;
      }
    }
  },
};
</script>

<style scoped>
.assign-to-col {
  min-width: 0;
}

.assign-to-fields {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.assign-to-role,
.assign-to-name {
  min-width: 160px;
  flex: 1 1 160px;
}

.start-due-date-col {
  max-width: 200px;
}

.date-field-append-icon {
  cursor: pointer;
  font-size: 22px !important;
}

.text-right {
  text-align: right;
}

.gap2 {
  gap: 15px;
}

.farm-location-col {
  min-width: 280px;
}

.farm-location-wrapper {
  cursor: pointer;
}

.farm-location-input {
  pointer-events: none;
}

.farm-location-input::v-deep input,
.farm-location-input::v-deep input::placeholder {
  color: #00BD73 !important;
  text-decoration: underline;
  opacity: 1;
}

.plot-marker-legend-icon {
  width: 40px;
  height: 40px;
  margin-right: 5px;
  object-fit: fill;
}

.plot-tree-status-legend.status-legend-section {
  background: #f8f9fa;
  border-radius: 10px;
  flex-wrap: wrap;
  font-size: 15px;
}

.plot-tree-status-legend .legend-title {
  color: #222;
}

.plot-tree-status-legend .legend-item {
  white-space: nowrap;
  font-size: 15px;
  color: #222;
}

.plot-tree-table::v-deep thead th {
  background-color: #287444 !important;
  color: #fff !important;
  font-weight: 600;
}

.plot-tree-map-wrapper {
  position: relative;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.plot-tree-map-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  margin: 0;
}

.plot-tree-map-wrapper--loading {
  opacity: 0.85;
}
</style>
